import * as React from 'react';
import { 
  TreeView, 
  TreeItem
} from '@mui/x-tree-view';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import {
  FolderOutlined as FolderIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { FolderWithChildren, CreateFolderRequest, UpdateFolderRequest } from '@/types/folders';
import { getRootFolders, createFolder, updateFolder, deleteFolder } from '@/api/folders';
import { useNavigate } from 'react-router-dom';

interface FolderTreeProps {
  onFolderSelect: (folderId: string | null) => void;
  selectedFolderId: string | null;
  refreshTrigger?: number;
}

const FolderTree: React.FC<FolderTreeProps> = ({ onFolderSelect, selectedFolderId, refreshTrigger = 0 }) => {
  const [folders, setFolders] = React.useState<FolderWithChildren[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<string[]>([]);
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [contextFolder, setContextFolder] = React.useState<FolderWithChildren | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [parentFolderId, setParentFolderId] = React.useState<string | null>(null);
  
  // Form states
  const [folderName, setFolderName] = React.useState('');
  const [folderDescription, setFolderDescription] = React.useState('');

  const navigate = useNavigate();

  const fetchFolders = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRootFolders();
      setFolders(data);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setError('Failed to load folders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchFolders();
  }, []);

  // Refresh folders whenever refreshTrigger changes
  React.useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('FolderTree refreshing due to trigger:', refreshTrigger);
      fetchFolders();
    }
  }, [refreshTrigger]);

  const handleNodeSelect = (_event: React.SyntheticEvent<HTMLUListElement, Event>): void => {
    console.log('handleNodeSelect called', _event);
    const target = _event.target as HTMLElement;
    const nodeId = target.getAttribute('data-node-id');
    console.log('Selected nodeId:', nodeId);
    if (!nodeId) return;
    
    if (nodeId === 'all') {
      // Prevent event default to avoid page refresh
      _event.preventDefault();
      onFolderSelect(null);
      navigate('/jobs', { state: {}, replace: true });
    } else {
      // Prevent event default to avoid page refresh
      _event.preventDefault();
      
      // Find the folder to get its name
      const findFolder = (folders: FolderWithChildren[], id: string): FolderWithChildren | null => {
        for (const folder of folders) {
          if (folder.id === id) return folder;
          if (folder.children) {
            const found = findFolder(folder.children, id);
            if (found) return found;
          }
        }
        return null;
      };
      
      const selectedFolder = findFolder(folders, nodeId);
      onFolderSelect(nodeId);
      
      // Update the location state with the folder name using navigate
      if (selectedFolder) {
        navigate('/jobs', { 
          state: { 
            selectedFolderId: nodeId,
            folderName: selectedFolder.name 
          },
          replace: true
        });
      }
    }
  };

  const handleToggle = (_event: React.SyntheticEvent<Element, Event>, nodeIds: string[]): void => {
    console.log('handleToggle called', nodeIds);
    setExpanded(nodeIds);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, folder: FolderWithChildren): void => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setContextFolder(folder);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  // Create folder handlers
  const handleCreateClick = (parentId: string | null = null): void => {
    setParentFolderId(parentId);
    setFolderName('');
    setFolderDescription('');
    setCreateDialogOpen(true);
    handleMenuClose();
  };

  const handleCreateFolder = async (): Promise<void> => {
    try {
      const newFolder: CreateFolderRequest = {
        name: folderName,
        description: folderDescription || undefined,
        parent: parentFolderId
      };
      
      await createFolder(newFolder);
      fetchFolders();
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Failed to create folder. Please try again.');
    }
  };

  // Edit folder handlers
  const handleEditClick = (): void => {
    if (contextFolder) {
      setFolderName(contextFolder.name);
      setFolderDescription(contextFolder.description || '');
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleUpdateFolder = async (): Promise<void> => {
    if (!contextFolder) return;
    
    try {
      const updateData: UpdateFolderRequest = {
        name: folderName,
        description: folderDescription || undefined
      };
      
      await updateFolder(contextFolder.id, updateData);
      fetchFolders();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating folder:', error);
      setError('Failed to update folder. Please try again.');
    }
  };

  // Handle menu actions
  const handleMenuAction = (action: string) => {
    if (!contextFolder) return;

    // Close the menu first
    setMenuAnchorEl(null);

    switch (action) {
      case 'edit':
        handleEditClick();
        break;
      case 'delete':
        setDeleteDialogOpen(true);  // Show the confirmation dialog
        break;
      default:
        break;
    }
  };

  // Handle folder deletion
  const handleDeleteFolder = async () => {
    if (!contextFolder) {
      console.error('No folder selected for deletion');
      return;
    }
    
    try {
      console.log('Deleting folder:', contextFolder.id);
      await deleteFolder(contextFolder.id);
      
      // If the deleted folder was selected, reset the selection
      if (selectedFolderId === contextFolder.id) {
        onFolderSelect(null);
        navigate('/jobs', { 
          state: { 
            selectedFolderId: null,
            folderName: null 
          },
          replace: true
        });
      }
      
      // Close the dialog first
      setDeleteDialogOpen(false);
      
      // Clear the context folder
      setContextFolder(null);
      
      // Then refresh the folder list
      await fetchFolders();
      
    } catch (error) {
      console.error('Error deleting folder:', error);
      setError('Failed to delete folder. Please try again.');
      // Keep the dialog open if there's an error
    }
  };

  // Add a function to handle folder tree click
  const handleTreeClick = React.useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    // Only refresh if we're clicking on the tree itself, not a button
    const target = e.target as HTMLElement;
    if (target.tagName !== 'BUTTON' && !target.closest('button')) {
      await fetchFolders();
    }
  }, []);

  // Render folder tree recursively
  const renderFolder = (folder: FolderWithChildren): JSX.Element => {
    return (
      <TreeItem
        key={folder.id}
        itemId={folder.id}
        onClick={(e) => {
          console.log('Folder clicked:', folder.id);
          // Prevent browser default behavior
          e.preventDefault();
          e.stopPropagation();
          onFolderSelect(folder.id);
          
          // Update location state with both folder ID and name
          navigate('/jobs', { 
            state: { 
              selectedFolderId: folder.id,
              folderName: folder.name 
            },
            replace: true
          });
        }}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, pr: 0 }}>
            <FolderIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'inherit', flexGrow: 1 }}>
              {folder.name} {folder.job_count > 0 && `(${folder.job_count})`}
            </Typography>
            <IconButton
              size="small"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleMenuOpen(e, folder)}
              sx={{ visibility: 'visible' }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        {folder.children?.map(child => renderFolder(child))}
        
        {/* Add subfolder button */}
        <TreeItem
          itemId={`add-to-${folder.id}`}
          onClick={(e) => {
            e.stopPropagation();
            handleCreateClick(folder.id);
          }}
          label={
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 0.5, 
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' } 
              }}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2">Add Subfolder</Typography>
            </Box>
          }
        />
      </TreeItem>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box onClick={handleTreeClick}>
      <TreeView
        aria-label="folder tree"
        defaultExpandedItems={expanded}
        selectedItems={selectedFolderId || 'all'}
        onSelect={handleNodeSelect}
        onExpandedItemsChange={handleToggle}
        slots={{
          expandIcon: ChevronRightIcon,
          collapseIcon: ExpandMoreIcon,
        }}
        sx={{ 
          height: '100%',
          flexGrow: 1,
          maxWidth: 400,
          overflowY: 'auto',
          '& .MuiTreeItem-content': {
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& .Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
        }}
      >
        <TreeItem
          itemId="all"
          onClick={(e) => {
            // Prevent browser default behavior
            e.preventDefault();
            e.stopPropagation();
            onFolderSelect(null);
            navigate('/jobs', { state: {}, replace: true });
          }}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
              <FolderIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">All Jobs</Typography>
            </Box>
          }
        />
        {folders.map(folder => renderFolder(folder))}
      </TreeView>
      
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('delete')}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
        <MenuItem onClick={() => handleCreateClick(contextFolder?.id || null)}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          Add Subfolder
        </MenuItem>
      </Menu>
      
      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={folderDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateFolder} 
            variant="contained"
            disabled={!folderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Folder Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={folderDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateFolder} 
            variant="contained"
            disabled={!folderName.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Folder Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the folder "{contextFolder?.name}"?
            {contextFolder?.job_count ? ` It contains ${contextFolder.job_count} jobs which will be unassigned.` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteFolder} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FolderTree; 